import { getProviders, signIn } from "next-auth/react";
import Image from "next/legacy/image";
import { useRouter } from "next/router";
import { NextSeo } from "next-seo";

import { FaFacebook, FaApple } from "react-icons/fa";
import { FcGoogle } from "react-icons/fc";
import { ExclamationIcon } from "@heroicons/react/solid";
import {
  asanteKotokoLogo,
  blurDataURL,
  loginPageBackgroundImageUrl,
} from "../../utils/constants/images";
import {
  APPLE_PROVIDER,
  FACEBOOK_PROVIDER,
  GOOGLE_PROVIDER,
} from "../../utils/constants/names";
import LoadingSpinner from "../../components/common/LoadingSpinner";

export default function SignIn({ providers }) {
  const { error } = useRouter().query;

  const errors = {
    Signin: "Try signing with a different account.",
    OAuthSignin: "Try signing with a different account.",
    OAuthCallback: "Try signing with a different account.",
    OAuthCreateAccount: "Try signing with a different account.",
    EmailCreateAccount: "Try signing with a different account.",
    Callback: "Try signing with a different account.",
    OAuthAccountNotLinked:
      "To confirm your identity, sign in with the same account you used originally.",
    EmailSignin: "Check your email address.",
    CredentialsSignin:
      "Sign in failed. Check the details you provided are correct.",
    default: "Unable to sign in.",
  };

  const SignInError = ({ error }) => {
    const errorMessage = error && (errors[error] ?? errors.default);
    return (
      <div
        className="alert bg-red-100 rounded-md py-5 px-6 mb-3 text-sm text-red-700 inline-flex items-center w-full alert-dismissible fade show"
        role="alert"
      >
        <div className="flex">
          <div className="flex-shrink-0">
            <ExclamationIcon
              className="h-5 w-5 text-red-400"
              aria-hidden="true"
            />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Login error</h3>
            <div className="mt-2 text-sm text-red-700">
              <p>{errorMessage}</p>
            </div>
          </div>
        </div>
        <button
          type="button"
          className="btn-close box-content w-4 h-4 p-1 ml-auto text-red-900 border-none rounded-none opacity-50 focus:shadow-none focus:outline-none focus:opacity-100 hover:text-red-900 hover:opacity-75 hover:no-underline"
          data-bs-dismiss="alert"
          aria-label="Close"
        ></button>
      </div>
    );
  };

  return (
    <>
      <NextSeo title="Login"></NextSeo>

      <main className="relative py-[50vh]">
        <div className="max-w-screen-xl mx-auto pb-6 px-4 sm:px-6 lg:pb-16 lg:px-8">
          <Image
            src={loginPageBackgroundImageUrl}
            alt="Payers celebrating league win"
            objectFit="cover"
            layout="fill"
            quality={100}
            placeholder="blur"
            blurDataURL={blurDataURL}
            className="relative h-full w-full object-cover"
          />
          <div
            className="w-full h-full absolute top-0 bottom-0 left-0 right-0 bg-gradient-to-b from-black via-black to-black opacity-[0.8]"
            aria-hidden="true"
          ></div>

          <div className="absolute top-[23vh] bottom-[30vh] sm:top-[30vh] sm:bottom-[30vh] md:top-[35vh] md:bottom-[35vh] lg:top-[35vh] lg:bottom-[35vh] xl:top-[30vh] xl:bottom-[30vh] 2xl:top-[35vh] 2xl:bottom-[35vh] left-6 right-6 sm:px-6 lg:px-8">
            <div className="p-8 bg-gray-100 rounded-md max-w-6xl pb-10 w-76 h-90 sm:mx-auto sm:w-full sm:max-w-md ">
              <div className="relative flex justify-center mx-auto mb-4 w-16 h-16">
                <Image
                  src={asanteKotokoLogo}
                  alt="Asante Kotoko Logo"
                  objectFit="contain"
                  quality={100}
                  layout="fill"
                  priority
                />
              </div>

              <h2 className="mt-6 mb-6 text-center text-2xl font-extrabold text-gray-900">
                Log in to your account
              </h2>
              {/* Error message */}
              {error && <SignInError error={error} />}

              {providers &&
                Object.values(providers).map((provider) => (
                  <div key={provider.name}>
                    {(() => {
                      /* Facebook Signin */
                      if (provider.id === FACEBOOK_PROVIDER) {
                        return (
                          <button
                            onClick={() => signIn(provider.id)}
                            className="flex justify-center items-center py-3 px-4 h-12 mt-3 text-white w-full rounded-md shadow-sm text-sm font-medium bg-blue-800 hover:bg-blue-900"
                          >
                            <FaFacebook className="mr-2 h-6 w-6 sm:h-7 sm:w-7" />
                            Continue with Facebook
                          </button>
                        );
                      }

                      /* Google Signin */
                      if (provider.id === GOOGLE_PROVIDER) {
                        return (
                          <button
                            onClick={() => signIn(provider.id)}
                            className="flex justify-center items-center py-3 px-4 mt-3 text-black w-full rounded-md shadow-sm text-sm font-medium bg-white  hover:bg-gray-100 border-2 border-gray-900"
                          >
                            <FcGoogle className="mr-2 h-6 w-6 sm:h-7 sm:w-7" />
                            Continue with Google
                          </button>
                        );
                      }

                      /* Apple Signin */
                      if (provider.id === APPLE_PROVIDER) {
                        return (
                          <button
                            onClick={() => signIn(provider.id)}
                            className="flex justify-center items-center py-3 px-4 mt-3  text-white w-full rounded-md shadow-sm text-sm font-medium bg-gray-900 hover:bg-gray-800"
                          >
                            <FaApple className="mr-2 h-6 w-6 sm:h-8 sm:w-8" />
                            Continue with Apple
                          </button>
                        );
                      }
                    })()}
                  </div>
                ))}

              {!providers && <LoadingSpinner />}
            </div>
          </div>
        </div>
      </main>
    </>
  );
}

export async function getServerSideProps(context) {
  const providers = await getProviders();
  return {
    props: { providers },
  };
}
